import { COLORS } from '@src/constants/design-tokens/colors';
import { announce } from '@src/utils/a11yAnnouncer';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * フォーカス管理フック
 *
 * ページ遷移時のフォーカス管理を行い、
 * キーボードナビゲーションのユーザビリティを向上させます。
 *
 * @param {Object} options - オプション設定
 * @returns {Object} フォーカス管理用のref
 */
export const useFocusManagement = (options?: {
  focusDelay?: number;
  scrollToTop?: boolean;
  announcePageChange?: boolean;
  focusTarget?: string;
}) => {
  const location = useLocation();
  const mainContentRef = useRef<HTMLElement | null>(null);
  const previousPathRef = useRef<string>('');

  const {
    focusDelay = 100,
    scrollToTop = true,
    announcePageChange = true,
    focusTarget = 'main-content',
  } = options || {};

  useEffect(() => {
    // 同じパスへの遷移は無視
    if (location.pathname === previousPathRef.current) {
      return;
    }

    previousPathRef.current = location.pathname;

    // フォーカスターゲットを探す
    const findFocusTarget = (): HTMLElement | null => {
      // 1. ID指定のターゲットを探す
      if (focusTarget) {
        const target = document.getElementById(focusTarget);
        if (target) {
          return target;
        }
      }

      // 2. メインコンテンツエリアを探す
      const main = document.querySelector('main, [role="main"]') as HTMLElement;
      if (main) {
        return main;
      }

      // 3. 最初の見出しを探す
      const heading = document.querySelector('h1, h2, [role="heading"]') as HTMLElement;
      if (heading) {
        return heading;
      }

      // 4. body要素にフォーカス
      return document.body;
    };

    // フォーカス設定とスクロール
    const setFocusAndScroll = () => {
      const target = findFocusTarget();

      if (target) {
        // tabindexを一時的に設定（既存の値を保存）
        const originalTabIndex = target.getAttribute('tabindex');
        if (!originalTabIndex) {
          target.setAttribute('tabindex', '-1');
        }

        // フォーカスを設定
        target.focus({ preventScroll: !scrollToTop });

        const shouldHighlight = !target.matches('main, [role="main"], body, html');

        if (shouldHighlight) {
          target.style.outline = `3px solid ${COLORS.primary.main}`;
          target.style.outlineOffset = '2px';
        }

        // フォーカス後にtabindexとアウトラインを戻す
        setTimeout(
          () => {
            if (!originalTabIndex) {
              target.removeAttribute('tabindex');
            }
            if (shouldHighlight) {
              target.style.outline = '';
              target.style.outlineOffset = '';
            }
          },
          shouldHighlight ? 2000 : 0
        );

        mainContentRef.current = target;
      }

      // パフォーマンス最適化: 既にトップにいる場合はスクロールをスキップ
      if (scrollToTop && window.scrollY > 0) {
        // スムーススクロールではなく即座にスクロール（パフォーマンス向上）
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    };

    // ページ変更をアナウンス（パフォーマンス最適化：共通ユーティリティを使用）
    if (announcePageChange) {
      const pageTitle = document.title || location.pathname;
      announce(`ページが読み込まれました: ${pageTitle}`, 'polite', 3000);
    }

    // フォーカス設定を遅延実行（コンテンツ読み込み待ち）
    const focusTimeout = setTimeout(() => {
      setFocusAndScroll();
    }, focusDelay);

    return () => {
      clearTimeout(focusTimeout);
    };
  }, [location, focusDelay, scrollToTop, announcePageChange, focusTarget]);

  return {
    mainContentRef,
  };
};
